import { VegaChartConfigDto } from '@asset-metrix/portal-gateway-client-js';
import { useQuery } from '@tanstack/react-query';

import {
  fetchFooterData,
  FetchFooterDataQueryKey,
} from '../../api/portalGateway/footerData';
import { useFilterSelections } from '../../store/hooks/useFilterSelections';
import { prependRoleId } from '../../utils/prependRoleId';

type ChartGroup = {
  [key: string]: string | number | boolean | null;
};

export const useFooterChartData = (
  widgetId: string,
  pageFilterInstanceId: string,
  widgetInstanceId: string,
  enabled: boolean,
  isGroupDataConfigured: boolean,
  entityConfig: VegaChartConfigDto
) => {
  const { groupedColumnForChart, tableFooterTitle } = entityConfig;
  const pageFilterSelections = useFilterSelections(pageFilterInstanceId);
  const tableFilterSelections = useFilterSelections(widgetInstanceId);
  const {
    data: footerData,
    isLoading,
    error,
  } = useQuery({
    queryKey: prependRoleId<FetchFooterDataQueryKey>([
      'footerDataForVega',
      widgetId,
      pageFilterSelections,
      tableFilterSelections,
      'NON_IRR',
    ]),
    queryFn: fetchFooterData,
    enabled,
  });

  /**
   * Function to transform footer table data to vega chart data
   * Footer data:
   * {
   *    columns: { value_x: 6 },
   *    groups: { value_x: { EUR: 1, GBP: 1, USD: 1 }, value_y: { EUR: 1.1 } }
   * }
   *
   * Chart data:
   *  { footerTitle: 'footer_title', value_x: 6},
   */
  const transformFooterDataToChartData = () => {
    if (footerData?.data.columns) {
      const chartFooterData: ChartGroup[] = [
        {
          footerTitle: tableFooterTitle || 'Total',
        },
      ];
      for (const key of Object.keys(footerData.data.columns)) {
        chartFooterData[0][key] = footerData.data.columns[key];
      }

      return chartFooterData;
    }
  };

  /**
   * Function to transform group table data to vega chart data
   * Footer data:
   * {
   *    columns: { value_x: 6 },
   *    groups: { value_x: { EUR: 1, GBP: 1, USD: 1 }, value_y: { EUR: 1.1 } }
   * }
   *
   * Chart data:
   * [
   *  { entity: 'EUR', value_x: 1, value_y: 1.1},
   *  { entity: 'GBP', value_x: 1},
   *  { entity: 'USD', value_x: 1}
   * ]
   * It uses the `groupedColumnForChart` as key for the group
   */
  const transformGroupDataToChartData = () => {
    const chartGroups: ChartGroup[] = [];
    const groups = footerData?.data.groups;
    if (groups && groupedColumnForChart) {
      for (const colName of Object.keys(groups)) {
        for (const groupName of Object.keys(groups[colName])) {
          const chartGroupIndex = chartGroups.findIndex(
            (g) => g[groupedColumnForChart] === groupName
          );
          const chartMetricValue = groups[colName][groupName] as
            | string
            | number;
          if (chartGroupIndex !== -1) {
            chartGroups[chartGroupIndex][colName] = chartMetricValue;
          } else {
            chartGroups.push({
              [groupedColumnForChart]: groupName,
              [colName]: chartMetricValue,
            });
          }
        }
      }
    }
    return chartGroups;
  };

  const transformedData = isGroupDataConfigured
    ? transformGroupDataToChartData()
    : transformFooterDataToChartData();

  return {
    transformedData,
    isLoading,
    error,
  };
};
